import {
  BehaviorSubject,
  combineLatest,
  concat,
  EMPTY,
  from,
  fromEventPattern,
  Observable,
  ReplaySubject,
  Subject,
  throwError
} from 'rxjs';
import {
  auditTime,
  catchError,
  filter,
  finalize,
  map,
  share,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import PouchDB from 'pouchdb';
import { environment } from '../../../environments/environment';

export declare namespace SyncedStorage {

  type Doc<TDocType extends {}> = PouchDB.Core.ExistingDocument<TDocType>;

  type PartialDoc<TDocType extends {}> = PouchDB.Core.IdMeta & Partial<Doc<TDocType>>;

  type SyncEventType = 'change' | 'complete' | 'paused' | 'active' | 'denied' | 'error';

  type SyncEvent<TDocType, TEventType extends SyncEventType> = {
    eventType: TEventType
  } & (
    TEventType extends 'change'
      ? PouchDB.Replication.SyncResult<TDocType> & { change: { pending: number } }
      : TEventType extends 'complete'
        ? PouchDB.Replication.SyncResultComplete<TDocType>
        : TEventType extends 'error' | 'paused' | 'denied'
          ? {}
          : any
    );

  type Sync<TDocType extends {} = any> = {
    db: PouchDB.Database,
    syncProcess: PouchDB.Replication.Sync<TDocType> | PouchDB.Replication.Replication<TDocType>,
    change$: Observable<PouchDB.Replication.SyncResult<TDocType>>,
    on$: <TEventType extends SyncEventType>(eventType?: TEventType) => Observable<SyncEvent<TDocType, TEventType>>
  };

  export type DocWatcher<TDocType extends {} = any> = Observable<Doc<TDocType>> & {
    _id: string,
    _docType: string
  };

  export type DocsWatcher<TDocType extends {} = any> = Observable<DocWatcher<TDocType>[]>;
}

/**
 * @TODO
 * Add CouchDB authentication:
 * @see https://github.com/pouchdb-community/pouchdb-authentication?tab=readme-ov-file#couchdb-authentication-recipe
 */
export class SyncedStorage<TAllDocTypes extends {} = any> {

  private static DEBUG: boolean = !environment?.production;

  private readonly dbName$ = new ReplaySubject<string>(1);

  public readonly db$: Observable<PouchDB.Database>;

  private docsWatchers: Record<string, SyncedStorage.DocsWatcher<TAllDocTypes>> = {};

  /**
   * A first subscription to any of the following observable streams starts the sync:
   * sync$, onLocalChange$, allDocs$, onSyncChange$, onSyncPull$, onSyncPush$.
   */

  /**
   * Starts sync on first subscription.
   * Emits the most recent SyncedStorage.Sync with every subscriber.
   * Stops the sync when all subscribers unsubscribe.
   * The observable completes when sync emits 'complete' event.
   */
  public readonly sync$: Observable<SyncedStorage.Sync<TAllDocTypes>>;

  /**
   * Everytime a doc changes in the local database, this observable emits that one single doc.
   */
  public readonly onLocalChange$: Observable<PouchDB.Core.ExistingDocument<TAllDocTypes> & PouchDB.Core.ChangesMeta>;

  public readonly allDocsFeed$: Observable<SyncedStorage.Doc<TAllDocTypes>[]>;

  // Emits all docs everytime any of them changes.
  public readonly allDocs$: Observable<SyncedStorage.Doc<TAllDocTypes>[]>;

  // Emits a change event (SyncResult) upon each sync event.
  public readonly onSyncChange$: Observable<PouchDB.Replication.SyncResult<TAllDocTypes>>;

  // Emits an array of changed documents upon each sync pull event.
  public readonly onSyncPull$: Observable<SyncedStorage.Doc<TAllDocTypes>[]>;

  // Emits an array of changed documents upon each sync push event.
  public readonly onSyncPush$: Observable<SyncedStorage.Doc<TAllDocTypes>[]>;

  constructor(dbName?: string, symmetrical?: boolean) {
    this.db$ = this.dbName$.pipe(
      switchMap((name) => {
        return SyncedStorage.createConnection$(name);
      })
    );

    this.sync$ = this.db$.pipe(
      switchMap((db: PouchDB.Database) => {
        return SyncedStorage.createSync$<TAllDocTypes>(db, !!symmetrical);
      }),
      tap((sync$) => SyncedStorage.log('sync$: ', sync$)),
      finalize(() => SyncedStorage.log('syncProcess$ STOP.')),
      shareReplay({
        refCount: true,
        bufferSize: 1
      })
    );

    this.onSyncChange$ = this.sync$.pipe(
      switchMap((sync: SyncedStorage.Sync<TAllDocTypes>) => sync.change$),
      finalize(() => SyncedStorage.log('sync$ STOP.')),
      tap((change) => SyncedStorage.log('sync$: ', change)),
      share()
    );

    this.onSyncPull$ = this.onSyncChange$.pipe(
      filter((syncResult) => syncResult.direction === 'pull'),
      tap((syncEvent) => SyncedStorage.log('pull$: ', syncEvent)),
      map((syncEvent) => syncEvent.change.docs),
      finalize(() => SyncedStorage.log('pull$ STOP.')),
      share()
    );

    this.onSyncPush$ = this.onSyncChange$.pipe(
      filter((syncResult) => syncResult.direction === 'push'),
      tap((syncEvent) => SyncedStorage.log('push$: ', syncEvent)),
      map((syncEvent) => syncEvent.change.docs),
      finalize(() => SyncedStorage.log('push$ STOP.')),
      share()
    );

    this.onLocalChange$ = this.sync$.pipe(
      switchMap((sync: SyncedStorage.Sync<TAllDocTypes>) => SyncedStorage.createChangesWatcher$<TAllDocTypes>(sync.db)),
      share()
    );

    this.allDocsFeed$ = this.sync$.pipe(
      switchMap((sync: SyncedStorage.Sync<TAllDocTypes>) => concat(
        SyncedStorage.getDocs$<TAllDocTypes>(sync.db),
        this.onLocalChange$.pipe(
          map((change) => ([change])) // @TODO Buffer...
        )
      )),
      share()
    );

    this.allDocs$ = this.watchType$();

    if (dbName !== undefined) {
      this.changeDb(dbName);
    }
  }

  public changeDb(dbName: string) {
    this.dbName$.next(dbName);
    return this.db$;
  }

  /**
   * Emits a doc (if exists) and then repeats it every time one the doc changes.
   * Subscribe to always receive up-to-date version of a doc.
   */
  watchId$<TDocType extends TAllDocTypes = TAllDocTypes>(_idOrDocType: string, id?: number | string): Observable<SyncedStorage.Doc<TDocType>> {
    let _id = SyncedStorage.toTypedDocId(_idOrDocType, id);
    return this.sync$.pipe(
      switchMap((sync) => {
        return concat(
          SyncedStorage.getDoc$<TDocType>(sync.db, _id).pipe(
            catchError(() => EMPTY)
          ),
          this.onLocalChange$.pipe(
            filter((doc: SyncedStorage.Doc<TDocType>) => doc._id === _id)
          ),
        );
      }),
      shareReplay({
        refCount: true,
        bufferSize: 1
      })
    );
  }

  /**
   * Emits an array of all docs of {docType} type (or all docs, in case the {docType} is logically negative) and then repeats it every on every `change` event (optionally on the {docType} docs only).
   * Subscribe to always receive up-to-date version of all docs (of the {docType}) array.
   */
  watchType$<TDocType extends TAllDocTypes = TAllDocTypes>(docType?: string, latency?: number): Observable<SyncedStorage.Doc<TDocType>[]> {
    return this.onLocalChange$.pipe(
      filter((doc) => !docType || SyncedStorage.isDocType(docType, doc._id)),
      startWith([]),
      auditTime(latency || 0), // Slows down the frequency of emissions.
      withLatestFrom(this.sync$),
      switchMap(([doc, sync]) => SyncedStorage.getDocs$<TDocType>(sync.db, docType)),
      shareReplay({
        refCount: true,
        bufferSize: 1
      })
    );
  }

  /**
   * @throws Error when the doc is not found in the DocsWatcher.
   */
  static watchDoc$<TDocType extends {}>(
    docs$$: SyncedStorage.DocsWatcher,
    _idOrDocType: string,
    id?: string | number
  ): Observable<SyncedStorage.Doc<TDocType>> {
    let _id = SyncedStorage.toTypedDocId(_idOrDocType, id);
    return docs$$.pipe(
      map((docWatchers) => docWatchers
        .find((docWatcher) => docWatcher._id === _id)
      ),
      // @TODO Handle "not found"
      // tap((docWatcher) => {
      //   if (!docWatcher) throw new Error('Watcher for doc "' + _id + '" not found.')
      // }),
      filter((docWatcher) => !!docWatcher),
      switchMap((docWatcher) => docWatcher),
      shareReplay({
        refCount: true,
        bufferSize: 1
      })
    );
  }

  // @see https://stackoverflow.com/questions/77723800/how-to-derive-types-from-strings-in-typescript
  static watchDocs$<TDocType extends {}>(
    docs$$: SyncedStorage.DocsWatcher,
    docType?: string
  ): Observable<SyncedStorage.Doc<TDocType>[]> {
    return docs$$.pipe(
      auditTime(0),
      (!docType
          ? switchMap((docWatchers) => combineLatest(docWatchers))
          : switchMap((docWatchers) => combineLatest(
            docWatchers.filter((docWatcher) => docWatcher._docType === docType)
          ))
      ),
      shareReplay({
        refCount: true,
        bufferSize: 1
      })
    );
  }

  getDocsWatcher<TDocTypes extends TAllDocTypes = TAllDocTypes>(docType?: string): SyncedStorage.DocsWatcher<TDocTypes> {
    if (!((docType || '') in this.docsWatchers)) {
      this.docsWatchers[docType || ''] = this.createDocsWatcher<TDocTypes>(docType);
    }
    return this.docsWatchers[docType || ''] as SyncedStorage.DocsWatcher<TDocTypes>;
  }

  private createDocsWatcher<TDocTypes extends TAllDocTypes = TAllDocTypes>(docType?: string): SyncedStorage.DocsWatcher<TDocTypes> {
    return this.sync$.pipe(
      switchMap((sync) => {
        return SyncedStorage.getDocs$<TDocTypes>(sync.db, docType).pipe(
          switchMap((allDocs) => {
            let record: Record<string, BehaviorSubject<SyncedStorage.Doc<TDocTypes>> & SyncedStorage.DocWatcher<TDocTypes>> = {};
            allDocs.forEach(doc => {
              let watcher: any = new BehaviorSubject<SyncedStorage.Doc<TDocTypes>>(doc);
              watcher._id = doc._id;
              watcher._docType = SyncedStorage.toDocType(doc._id);
              record[doc._id] = watcher;
            });
            return (
              docType ? this.onLocalChange$.pipe(
                filter((doc) => SyncedStorage.isDocType(docType, doc._id))
              ) : this.onLocalChange$
            ).pipe(
              filter((doc: PouchDB.Core.ExistingDocument<TDocTypes> & PouchDB.Core.ChangesMeta) => {
                if (!(doc._id in record)) { // A new doc appears...
                  if (doc._deleted) {
                    return false; // Ignores new docs that have been already deleted. Blocks record update emission.
                  }
                  let watcher: any = new BehaviorSubject<SyncedStorage.Doc<TDocTypes>>(doc);
                  watcher._id = doc._id;
                  watcher._docType = SyncedStorage.toDocType(doc._id);
                  record[doc._id] = watcher; // Adds new doc to the record.
                } else if (!doc._deleted) { // An existing doc changes...
                  record[doc._id].next(doc); // Emits doc update.
                  return false; // Blocks record update emission. Only the doc itself emits.
                } else {
                  record[doc._id].next(doc); // Emits doc deletion. @TODO Delete it?
                  record[doc._id].complete();
                  delete record[doc._id];
                }
                return true; // Triggers record update emission.
              }),
              map(() => Object.values(record)),
              startWith(Object.values(record))
            );
          })
        );
      }),
      shareReplay({
        refCount: true,
        bufferSize: 1
      })
    );
  }

  private static createConnection$(dbName: string): Observable<PouchDB.Database> {
    return fromEventPattern<PouchDB.Database>(
      (next) => {
        const db = new PouchDB(dbName, { auto_compaction: true, skip_setup: true });
        next(db);
        return db;
      },
      (handler, db: PouchDB.Database) => {
        db.close();
      }
    ).pipe(
      shareReplay({
        refCount: true,
        bufferSize: 1
      })
    );
  }

  private static createSync$<TDocType extends {} = any>(
    db: PouchDB.Database,
    symmetrical?: boolean
  ): Observable<SyncedStorage.Sync<TDocType>> {
    const batch_size = 500;
    const batches_limit = 10;

    return fromEventPattern<SyncedStorage.Sync<TDocType>>(
      (next): SyncedStorage.Sync<TDocType> => {
        SyncedStorage.log('START SYNC PROCESS');

        let syncProcess: PouchDB.Replication.Sync<TDocType> | PouchDB.Replication.Replication<TDocType>;
        let options: PouchDB.Replication.SyncOptions = {
          live: true,
          retry: true,
          // style: 'main_only',
          batch_size,
          batches_limit
        };
        let syncResult$ = new ReplaySubject<PouchDB.Replication.SyncResult<TDocType>>(1);
        let syncEvent$: Subject<SyncedStorage.SyncEvent<TDocType, SyncedStorage.SyncEventType>> = new Subject<SyncedStorage.SyncEvent<TDocType, SyncedStorage.SyncEventType>>();
        let on$ = (eventType?: SyncedStorage.SyncEventType) => syncEvent$.pipe(
          filter((event) => !eventType || event.eventType === eventType),
          share()
        );

        if (symmetrical) {
          syncProcess = db.sync<TDocType>(environment.pouchDbUrl + db.name, options)
            .on('change', (result: PouchDB.Replication.SyncResult<TDocType>) => {
              SyncedStorage.log('SYNC change: ', result);
              syncEvent$.next({ ...result, eventType: 'change' });
              syncResult$.next(result);
            })
            .on('complete', (result: PouchDB.Replication.SyncResultComplete<TDocType>) => {
              SyncedStorage.log('SYNC complete: ', result);
              syncEvent$.next({ ...result, eventType: 'complete' });
              syncResult$.complete();
            })
            .on('paused', (err) => {
              SyncedStorage.log('SYNC paused: ', err);
              syncEvent$.next({ ...err, eventType: 'paused' });
            })
            .on('active', () => {
              SyncedStorage.log('SYNC active');
              syncEvent$.next({ eventType: 'active' });
            })
            .on('denied', (err) => {
              SyncedStorage.log('SYNC denied: ', err);
              syncEvent$.next({ ...err, eventType: 'denied' });
            })
            .on('error', (err) => {
              SyncedStorage.log('SYNC error: ', err);
              syncEvent$.next({ ...err, eventType: 'error' });
            });
        } else {
          syncProcess = db.replicate.from<TDocType>(environment.pouchDbUrl + db.name, options)
            .on('change', (result: PouchDB.Replication.ReplicationResult<TDocType>) => {
              SyncedStorage.log('REPLICATION change: ', result);
              let syncResult = {
                direction: 'pull',
                change: result
              } as PouchDB.Replication.SyncResult<TDocType>;
              syncEvent$.next({ ...syncResult, eventType: 'change' });
              syncResult$.next(syncResult);
            })
            .on('complete', (result: PouchDB.Replication.ReplicationResultComplete<TDocType>) => {
              SyncedStorage.log('REPLICATION complete: ', result);
              syncEvent$.next({ ...result, eventType: 'complete' });
              syncResult$.complete();
            }).on('paused', (err) => {
              SyncedStorage.log('REPLICATION paused: ', err);
              syncEvent$.next({ ...err, eventType: 'paused' });
            })
            .on('active', () => {
              SyncedStorage.log('REPLICATION active');
              syncEvent$.next({ eventType: 'active' });
            })
            .on('denied', (err) => {
              SyncedStorage.log('REPLICATION denied: ', err);
              syncEvent$.next({ ...err, eventType: 'denied' });
            })
            .on('error', (err) => {
              SyncedStorage.log('REPLICATION error: ', err);
              syncEvent$.next({ ...err, eventType: 'error' });
            });
        }
        let sync: SyncedStorage.Sync<TDocType> = {
          db,
          syncProcess,
          change$: syncResult$.asObservable(),
          on$
        };
        next(sync);
        return sync;
      },
      function(handler, sync: SyncedStorage.Sync<TDocType>) {
        SyncedStorage.log('STOP SYNC PROCESS');
        sync.syncProcess.cancel();
      }
    );
  }

  private static getDocs$<TDocTypes extends {} = any>(db: PouchDB.Database, docType?: string): Observable<PouchDB.Core.ExistingDocument<TDocTypes>[]> {
    if (!docType) {
      return from(db.allDocs({
        include_docs: true,
        // update_seq: true
      })).pipe(
        map((allDocs: PouchDB.Core.AllDocsResponse<TDocTypes>) => {
          return allDocs.rows.map(r => r.doc);
        })
      );
    } else {
      return from(db.allDocs({
        include_docs: true,
        // update_seq: true,
        startkey: docType,
        endkey: docType + '\ufff0'
      })).pipe(
        map((allDocs: PouchDB.Core.AllDocsResponse<TDocTypes>) => {
          return allDocs.rows.map(r => r.doc);
        })
      );
    }
  }

  private static getDoc$<TDocType extends {} = any>(db: PouchDB.Database, _id: string): Observable<PouchDB.Core.Document<TDocType> & PouchDB.Core.GetMeta> {
    return from(db.get<TDocType>(_id));
  }

  /**
   * TODO since
   */
  private static createChangesWatcher$<TContent>(
    db: PouchDB.Database,
    options: PouchDB.Core.ChangesOptions = {}
  ): Observable<PouchDB.Core.ExistingDocument<TContent> & PouchDB.Core.ChangesMeta> {
    return fromEventPattern<PouchDB.Core.ExistingDocument<TContent> & PouchDB.Core.ChangesMeta>(
      (next) => {
        return db.changes({
            since: 'now',
            live: true,
            conflicts: true,
            include_docs: true,
            ...options
          })
          .on('change', (change: PouchDB.Core.ChangesResponseChange<TContent>) => {
            next(change.doc);
          });
      },
      (complete, changesProcess: PouchDB.Core.Changes<TContent>) => {
        changesProcess.cancel();
      }
    ).pipe(
      share()
    );
  }

  save$<TModel extends TAllDocTypes = TAllDocTypes>(partialUpdateValue: SyncedStorage.PartialDoc<TModel>): Observable<PouchDB.Core.Response> {
    return this.sync$.pipe(
      switchMap((sync) => {
        return sync.db.get<TModel>(partialUpdateValue._id)
          .catch((reason) => {
            return partialUpdateValue as any;
          })
          .then((previousValue) => {
            let updateValue = {
              ...previousValue,
              ...partialUpdateValue
            };
            return sync.db.put<TModel>(updateValue);
          });
      }),
      take(1),
      tap((response) => {
        SyncedStorage.log('Saved: ', response);
      }),
    );
  }

  createSyncTerminator$(): Observable<SyncedStorage.Sync<TAllDocTypes>> {
    return this.sync$.pipe(
      take(1),
      tap((sync) => {
        sync.syncProcess.cancel();
      })
    );
  }

  createRemoteDbCompactor$(): Observable<any> {
    return this.dbName$.pipe(
      take(1),
      switchMap((dbName) => {
        SyncedStorage.log('Starting remote database "' + dbName + '" compaction.');
        let remoteDb = new PouchDB(environment.pouchDbUrl + dbName);
        return from(remoteDb.compact()).pipe(
          switchMap((response) => {
            return remoteDb.close();
          }),
          catchError((error) => {
            SyncedStorage.error('Remote database "' + dbName + '" compaction error: ', error);
            return throwError(error);
          }),
          finalize(() => SyncedStorage.log('Remote database "' + dbName + '" compaction completed.'))
        )
      })
    );
  }

  createLocalDbDeleter$(): Observable<string> {
    return this.createSyncTerminator$().pipe(
      take(1),
      switchMap((sync) => {
        return from(sync.db.destroy()).pipe(
          take(1),
          map(() => sync.db.name)
        );
      })
    );
  }

  public static toDocType(_id: string): string {
    return _id.split('#', 1)[0];
  }

  public static toUntypedDocId(_id: string): string {
    return _id.split('#', 2)[1] || _id;
  }

  public static toTypedDocId(_idOrDocType: string, id?: string | number): string {
    return id === undefined ? _idOrDocType : _idOrDocType + '#' + id;
  }

  public static isDocType(docType: string, _id: string): boolean {
    return _id.indexOf(docType) === 0;
  }

  public static generateTypedDocId(docType: string): string {
    return SyncedStorage.toTypedDocId(docType, (new Date).getTime());
  }

  static log(...args) {
    if (SyncedStorage.DEBUG) {
      console.log('[SyncedStorage]', ...args);
    }
  }

  static warn(...args) {
    if (SyncedStorage.DEBUG) {
      console.warn('[SyncedStorage]', ...args);
    }
  }

  static error(...args) {
    if (SyncedStorage.DEBUG) {
      console.log('[SyncedStorage]', ...args);
    }
  }
}
